import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Activity, AlertTriangle, Clock, Plug } from 'lucide-react'
import { PageHeader, StatCard } from '@/kit/ui'
import { batchOf, effectiveState } from '@/lib/flow'
import { useCodeStore } from '@/store/codeStore'
import { refreshEndpoints, useFlowStore } from '@/store/flowStore'
import { apiMode } from '@/lib/hubApi'
import { useHubStore } from '@/store/hubStore'
import { EndpointStateBadge } from '../lender/flow/EndpointsSubTab'

/** Active endpoints (F3-4): every endpoint across lenders as the web app sees it, with the last 24 hours. */
export function ActiveEndpointsScreen() {
  const endpoints = useFlowStore((s) => s.endpoints)
  const flows = useFlowStore((s) => s.flows)
  const lenders = useHubStore((s) => s.lenders)
  const batches = useCodeStore((s) => s.batches)
  // on the API the counters are the run log's: read again each time the screen opens
  useEffect(() => { if (apiMode()) void refreshEndpoints().catch(() => {}) }, [])
  const rows = useMemo(
    () =>
      endpoints
        .filter((e) => e.state !== 'retired')
        .map((e) => {
          const lender = lenders.find((l) => l.slug === e.lenderSlug)
          const product = lender?.products.find((p) => p.slug === e.productSlug)
          const flow = flows.find((f) => f.name === e.workflowName && f.version === e.workflowVersion)
          return { e, lender, product, flow, batch: flow ? batchOf(flow, batches) : undefined, state: effectiveState(e, lender, product) }
        }),
    [endpoints, flows, lenders, batches],
  )
  const runs = rows.reduce((n, r) => n + r.e.stats.runs24h, 0)
  const errors = rows.reduce((n, r) => n + r.e.stats.errors24h, 0)
  const withAvg = rows.filter((r) => r.e.stats.avgMs !== null && r.e.stats.runs24h > 0)
  const avg = withAvg.length ? withAvg.reduce((n, r) => n + r.e.stats.avgMs! * r.e.stats.runs24h, 0) / Math.max(1, withAvg.reduce((n, r) => n + r.e.stats.runs24h, 0)) : null
  const worst = rows.slice().sort((a, b) => b.e.stats.errors24h - a.e.stats.errors24h)[0]

  return (
    <>
      <PageHeader title="Active endpoints" subtitle="Every endpoint across lenders, as the web app sees it: the version behind it, the batch, the last 24 hours" />
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={Plug} label="endpoints" value={rows.length} hint={`${rows.filter((r) => r.state === 'live').length} live in production`} />
        <StatCard icon={Activity} label="runs in 24 h" value={runs} />
        <StatCard icon={Clock} label="average time" value={avg === null ? '—' : `${(avg / 1000).toFixed(1)} s`} tone="gray" />
        <StatCard icon={AlertTriangle} label="errors in 24 h" value={errors} hint={worst && worst.e.stats.errors24h ? `most: ${worst.lender?.name ?? worst.e.lenderSlug}` : undefined} tone={errors ? 'danger' : 'gray'} />
      </div>
      <div className="card-flush overflow-x-auto">
        <table className="table">
          <thead><tr><th>endpoint_id</th><th>Lender · product</th><th>Workflow</th><th>Batch</th><th>Env</th><th className="text-right">Runs 24 h</th><th className="text-right">Avg</th><th className="text-right">Errors</th><th>State</th></tr></thead>
          <tbody>
            {rows.map(({ e, lender, product, batch, state }) => (
              <tr key={e.id} data-testid={`active-ep-${e.id}`}>
                <td className="mono"><Link to={`/lenders/${e.lenderSlug}/api/endpoints?ep=${encodeURIComponent(e.id)}`}>{e.id}</Link></td>
                <td>{lender?.name ?? e.lenderSlug} · {product?.name ?? e.productSlug}</td>
                <td className="mono">{e.workflowName.split('.').slice(1).join('.')} {e.workflowVersion}</td>
                <td className="mono">{batch?.version ?? '—'}</td>
                <td>{e.env === 'uat' ? 'UAT only' : 'UAT + Prod'}</td>
                <td className="text-right">{e.stats.runs24h}</td>
                <td className="text-right">{e.stats.avgMs === null ? '—' : `${(e.stats.avgMs / 1000).toFixed(1)} s`}</td>
                <td className={`text-right ${e.stats.errors24h ? 'text-red-700 dark:text-red-300' : ''}`}>{e.stats.errors24h}</td>
                <td><EndpointStateBadge state={state} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-2xs text-muted">The state is what the app gets: the lender's single switch and the product switch are applied on top of what an admin set. Click an endpoint to switch its version, suspend it or send a test call.</p>
    </>
  )
}
